import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Building } from 'lucide-react'
import { Button } from '@/components/ui'
import { useBusinesses } from '@/hooks/useBusinesses'

export function BusinessNewPage() {
  const navigate = useNavigate()
  const { createBusiness } = useBusinesses()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    businessType: '',
    startDate: '',
    notes: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const payload = {
      name: formData.name,
      businessType: formData.businessType || undefined,
      currency: 'IDR' as const,
      startDate: formData.startDate || undefined,
      notes: formData.notes || undefined
    }

    const res = await createBusiness(payload)
    setIsLoading(false)

    if (res.success && res.data) {
      navigate(`/businesses/${res.data.id}`)
    } else {
      setError(res.error || 'Gagal membuat bisnis')
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/businesses" className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-100">Buat Bisnis Baru</h1>
          <p className="text-sm text-slate-400">Masukkan informasi dasar bisnis Anda</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5" htmlFor="name">
              Nama Bisnis <span className="text-brand-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Contoh: Kopi Senja"
              className="w-full h-10 px-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-100 text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5" htmlFor="businessType">
              Kategori / Jenis Bisnis
            </label>
            <input
              id="businessType"
              name="businessType"
              type="text"
              value={formData.businessType}
              onChange={handleChange}
              placeholder="Contoh: F&B, Retail, Jasa"
              className="w-full h-10 px-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-100 text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5" htmlFor="startDate">
              Tanggal Mulai (Opsional)
            </label>
            <input
              id="startDate"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full h-10 px-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-100 text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5" htmlFor="notes">
              Deskripsi / Catatan Singkat
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              placeholder="Deskripsi singkat tentang apa yang dijual..."
              className="w-full p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-100 text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors resize-none"
            />
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end gap-3">
            <Button onClick={() => navigate('/businesses')} variant="outline" type="button">
              Batal
            </Button>
            <Button type="submit" disabled={isLoading} className="gap-2">
              <Building size={16} />
              {isLoading ? 'Menyimpan...' : 'Simpan Bisnis'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
